import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Car, Users, HelpCircle, MessagesSquare, Briefcase, ShieldCheck, ArrowRight, ChevronDown, BadgeCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { RoadStrip } from "@/components/CarScene";

const FEATURES = [
  { icon: Car, title: "Ride together", desc: "Find or offer rides with verified people from your own institution — car, bike, auto or cab." },
  { icon: Users, title: "Find buddies", desc: "Build your circle of trusted campus buddies and travel with people you actually know." },
  { icon: HelpCircle, title: "Ask “Are You Going?”", desc: "Fest tomorrow? Ask your buddies, see who's going, and spin up a group ride in one tap." },
  { icon: MessagesSquare, title: "Campus community", desc: "Discussions, confessions, memes and polls — an institution-only feed with real moderation." },
  { icon: Briefcase, title: "Opportunities", desc: "Internships, projects, competitions and clubs shared inside your campus network." },
  { icon: ShieldCheck, title: "Safety first", desc: "Institution verification, driver verification, ratings, reporting and privacy controls." },
];

export default function Landing() {
  const { user } = useAuth();
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 20 });
  const sy = useSpring(my, { stiffness: 60, damping: 20 });
  const imgX = useTransform(sx, [-0.5, 0.5], [18, -18]);
  const imgY = useTransform(sy, [-0.5, 0.5], [12, -12]);
  const cardX = useTransform(sx, [-0.5, 0.5], [-24, 24]);
  const cardY = useTransform(sy, [-0.5, 0.5], [-16, 16]);
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set(e.clientX / r.width - 0.5);
    my.set(e.clientY / r.height - 0.5);
  };
  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-40 backdrop-blur-2xl bg-[#090A0B]/85 border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <span className="font-display font-black text-2xl text-[#22C55E]">THIRI</span>
          <div className="flex items-center gap-2">
            {user && user !== false ? (
              <Link to="/home" data-testid="landing-open-app" className="rounded-full bg-primary text-white px-5 py-2 text-sm font-bold active:scale-95 transition-transform duration-150">Open App</Link>
            ) : (
              <>
                <Link to="/login" data-testid="landing-login" className="px-4 py-2 text-sm font-bold text-white/80 hover:text-white transition-colors duration-200">Log in</Link>
                <Link to="/register" data-testid="landing-signup" className="rounded-full bg-primary text-white px-5 py-2 text-sm font-bold active:scale-95 transition-transform duration-150">Get Started</Link>
              </>
            )}
          </div>
        </div>
      </header>

      <section onMouseMove={onMove} className="relative min-h-screen bg-[#090A0B] text-white overflow-hidden flex items-center">
        <motion.img style={{ x: imgX, y: imgY, scale: 1.08 }}
          src="/auto-3d.png?v=1"
          alt="3D green auto-rickshaw with students" className="absolute inset-0 w-full h-full object-cover object-right opacity-90 car-bob" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#090A0B] via-[#090A0B]/60 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#090A0B] via-transparent to-[#090A0B]/30" />

        <motion.div style={{ x: cardX, y: cardY }} className="hidden lg:block absolute right-[8%] top-[24%] z-10">
          <motion.div animate={{ y: [0, -14, 0] }} transition={{ repeat: Infinity, duration: 4.5, ease: "easeInOut" }}
            className="glass rounded-2xl px-5 py-4 text-[#12211C] shadow-[0_16px_48px_rgba(0,0,0,0.4)]" data-testid="floating-match-card">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#1DAF5A]">Live match</p>
            <p className="font-display font-bold text-sm mt-1 flex items-center gap-2"><Car className="w-4 h-4 text-[#1DAF5A]" /> Miyapur → Campus · 92%</p>
          </motion.div>
        </motion.div>
        <motion.div style={{ x: cardY, y: cardX }} className="hidden lg:block absolute right-[16%] bottom-[26%] z-10">
          <motion.div animate={{ y: [0, 12, 0] }} transition={{ repeat: Infinity, duration: 5.5, ease: "easeInOut" }}
            className="glass rounded-2xl px-5 py-4 text-[#12211C] shadow-[0_16px_48px_rgba(0,0,0,0.4)]" data-testid="floating-buddy-card">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#1DAF5A]">Are you going?</p>
            <p className="font-display font-bold text-sm mt-1 flex items-center gap-2"><Users className="w-4 h-4 text-[#1DAF5A]" /> 3 buddies going to Fest</p>
          </motion.div>
        </motion.div>

        <div className="relative z-10 max-w-6xl mx-auto px-4 w-full pt-24 pb-20">
          <motion.span initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="t-chip bg-primary/15 text-[#4ADE80] border border-primary/30 backdrop-blur">
            <BadgeCheck className="w-3.5 h-3.5" /> Verified campus network · HMRITM
          </motion.span>
          <h1 className="mt-4 font-display font-black text-[22vw] md:text-[9rem] leading-[0.9] tracking-tighter text-[#22C55E] select-none" aria-label="THIRI">
            {"THIRI".split("").map((c, i) => (
              <motion.span key={`${c}${i}`} initial={{ y: 90, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.15 + i * 0.09, type: "spring", stiffness: 180, damping: 16 }}
                whileHover={{ y: -10, color: "#F9C31F" }} className="inline-block cursor-default">{c}</motion.span>
            ))}
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}
            className="mt-4 font-display font-bold text-xl md:text-2xl">
            Your institution. Your people. <span className="text-[#22C55E]">Your rides.</span>
          </motion.p>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85 }}
            className="mt-3 text-white/60 max-w-md leading-relaxed">
            Pool rides, find buddies, ask “Are you going?” — everything your campus moves on, verified.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1 }}
            className="flex flex-wrap gap-3 mt-8">
            <Link to="/register" data-testid="hero-get-started"
              className="inline-flex items-center gap-2 rounded-full bg-primary text-white px-8 py-4 font-bold shadow-[0_8px_40px_hsla(145,72%,40%,0.45)] hover:shadow-[0_8px_56px_hsla(145,72%,40%,0.65)] active:scale-95 transition-all duration-200">
              Get Started <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="#features" data-testid="hero-explore"
              className="inline-flex items-center gap-2 rounded-full border border-white/25 px-8 py-4 font-bold text-white/90 hover:bg-white/10 transition-colors duration-200">
              Explore THIRI
            </a>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }}
            className="grid grid-cols-3 gap-3 mt-12 max-w-md" data-testid="stat-strip">
            {[["100%", "verified members"], ["4", "vehicle types"], ["0", "numbers shared"]].map(([v, l]) => (
              <div key={l} className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-4 text-center">
                <p className="font-display font-black text-2xl text-[#22C55E]">{v}</p>
                <p className="text-[11px] text-white/50 mt-1">{l}</p>
              </div>
            ))}
          </motion.div>
        </div>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}
          className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-white/50">
          <ChevronDown className="w-6 h-6" />
        </motion.div>
      </section>

      <section className="bg-[#090A0B] pb-24">
        <div className="max-w-5xl mx-auto px-4">
          <span className="t-chip bg-primary/15 text-[#4ADE80] border border-primary/30">Carpooling, the THIRI way</span>
          <h2 className="font-display font-bold text-lg md:text-xl text-white mt-4">One car. Three buddies. Zero boring rides.</h2>
          <motion.div initial={{ opacity: 0, y: 40, scale: 0.96 }} whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true }} transition={{ duration: 0.8, ease: "easeOut" }}
            className="relative mt-10 rounded-[2rem] overflow-hidden border border-white/10 shadow-[0_32px_96px_-24px_rgba(29,175,90,0.35)] group"
            data-testid="carpool-scene">
            <div className="absolute -inset-24 bg-primary/10 blur-3xl pointer-events-none" />
            <img src="/carpool-3d.png?v=6" alt="3D realistic carpooling scene — students in a green car"
              className="w-full object-cover car-bob" />
            <div className="shine-sweep pointer-events-none" />
            <div className="absolute inset-x-0 top-6 md:top-10 text-center px-4 pointer-events-none" data-testid="carpool-slogan">
              {["Share the ride.", "Split the fare.", "Make a friend."].map((w, i) => (
                <motion.span key={w} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ delay: 0.4 + i * 0.45, duration: 0.5 }}
                  className={`block md:inline md:mx-2 font-display font-black text-2xl md:text-4xl drop-shadow-[0_4px_20px_rgba(0,0,0,0.9)] ${i === 1 ? "text-[#4ADE80]" : "text-[#22C55E]"}`}>
                  {w}
                </motion.span>
              ))}
            </div>
            <div className="absolute bottom-5 right-5 glass rounded-full px-4 py-2 text-xs font-bold text-[#12211C] shadow-lg"
              data-testid="carpool-chip-2">
              95% match · ₹40/seat
            </div>
          </motion.div>
        </div>
      </section>

      <RoadStrip />

      <section className="py-16 overflow-hidden bg-white border-b border-black/5">
        <div className="max-w-5xl mx-auto px-4 mb-8">
          <p className="t-chip bg-primary/10 text-primary">Campus life, in motion</p>
        </div>
        <div className="flex gap-6 w-max marquee" data-testid="photo-marquee">
          {[0, 1].map((dup) => (
            <div key={dup} className="flex gap-6">
              {[
                ["https://images.pexels.com/photos/3753039/pexels-photo-3753039.jpeg", "Friends sharing a ride"],
                ["https://images.unsplash.com/photo-1674216709275-61a436e1ecd5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwzfHxpc29tZXRyaWMlMjBjaXR5JTIwY2FyfGVufDB8fHx8MTc4NzQ3NTMxNXww&ixlib=rb-4.1.0&q=85", "3D car"],
                ["https://images.pexels.com/photos/8199169/pexels-photo-8199169.jpeg", "Students on campus"],
                ["https://images.unsplash.com/photo-1676748219706-336eaedb5c1d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHw0fHxpc29tZXRyaWMlMjBjaXR5JTIwY2FyfGVufDB8fHx8MTc4NzQ3NTMxNXww&ixlib=rb-4.1.0&q=85", "3D city ride"],
              ].map(([src, alt]) => (
                <img key={src} src={src} alt={alt}
                  className="h-56 w-80 object-cover rounded-2xl border border-black/5 hover:scale-[1.04] transition-transform duration-300" />
              ))}
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 pt-16 pb-20">
        <p className="t-chip bg-primary/10 text-primary mb-4">How it works</p>
        <div className="grid sm:grid-cols-3 gap-6">
          {[["1", "Verify your campus", "Prove you belong to HMRITM with your institution email OTP or ID card."],
            ["2", "Find your match", "Search rides by pickup, destination and time — see a live match score for each."],
            ["3", "Ride together", "Request a seat, chat in-app, travel with verified people, rate after the ride."]].map(([n, t, d]) => (
            <div key={n} className="t-card p-6 hover-lift">
              <span className="w-9 h-9 rounded-full bg-primary text-white font-display font-bold flex items-center justify-center">{n}</span>
              <h3 className="font-display font-bold mt-4">{t}</h3>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="features" className="max-w-5xl mx-auto px-4 pb-24">
        <p className="t-chip bg-secondary/20 text-[#14532D] mb-4">Why THIRI</p>
        <h2 className="font-display font-bold text-lg md:text-xl mb-8">Everything your campus life moves on</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {FEATURES.map(({ icon: Icon, title, desc }, i) => (
            <motion.div key={title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.05 }}
              className="t-card p-6 hover-lift">
              <div className="w-11 h-11 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
                <Icon className="w-5 h-5 text-primary" />
              </div>
              <h3 className="font-display font-bold">{title}</h3>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="bg-primary/10 py-16">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <p className="t-chip bg-primary text-white mx-auto">The THIRI pledge</p>
          <h2 className="font-display font-bold text-lg md:text-xl mt-4 max-w-2xl mx-auto">
            Every shared seat means one less vehicle on the road — and one more campus friend.
          </h2>
          <p className="text-sm text-muted-foreground mt-2">Stop riding alone.</p>
          <Link to="/register" data-testid="pledge-cta"
            className="inline-flex items-center gap-2 rounded-full bg-primary text-white px-7 py-3.5 font-bold mt-6 hover-lift">
            Join your campus <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <footer className="border-t border-black/5 py-8 text-center text-sm text-muted-foreground">
        <span className="font-display font-black text-primary">THIRI</span> · thiri.in · Your institution. Your people. Your rides.
      </footer>
    </div>
  );
}
